/* eslint-disable react/require-default-props */

import React, { useEffect, useRef } from 'react';
import proptypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { useNavigate } from '@thd-olt-component-react/router';
import { BundleAlertmessage } from './subcomponents/BundleAlertmessage';
import { BundleVariantChangeAlert } from './subcomponents/BundleVariantChangeAlert';
import {
  isBundleUnavailable,
  bundleHasOOSProduct,
  detectAndReportBundleDataIssues,
  getUnavailableAlertMessage
} from '../utils/product-unavailable-utils';

const { shape, string, arrayOf, number } = proptypes;

const BundleAlert = ({
  product,
  alertSize = 'small',
  alertBodyText = null,
  alertHeadline = null,
  products,
  accessories = []
}) => {

  const {
    hidePrice, brandUrl, shopMoreLinkText, unavailableBundleMessageText, unavailableAlertStatus,
    unavailableAlertAdditionalText, unavailableAlertHeadline, unavailableShopMoreLink
  } = product?.features || {};
  const hasOOsProduct = bundleHasOOSProduct({ products });
  const isUnavailable = !hasOOsProduct && hidePrice && isBundleUnavailable({ products });

  const navigate = useNavigate();

  const checkedItemIdsRef = useRef(new Set());

  useEffect(() => {
    if (!products?.length) {
      return;
    }

    detectAndReportBundleDataIssues({
      products,
      checkedItemIdsRef: checkedItemIdsRef.current
    });
  }, [products]);

  const navigateToPackage = () => {
    navigate(unavailableShopMoreLink || brandUrl);
  };

  if (isUnavailable) {
    return (
      <div className="sui-w-full sui-flex sui-flex-col sui-gap-4" data-testid="bundle-unavailable">
        <Typography variant="h2">{unavailableBundleMessageText}</Typography>
        <BundleAlertmessage
          bundleProduct={product}
          size="large"
          content={{ main: unavailableAlertAdditionalText }}
          headline={unavailableAlertHeadline}
          status={unavailableAlertStatus}
          link={{
            show: !!shopMoreLinkText,
            text: shopMoreLinkText,
            newLine: false,
            onClick: navigateToPackage
          }}
        />
      </div>
    );
  }

  const accessoriesList = accessories?.[0]?.offerings?.map((offering) => offering.products).flat() || [];
  const unavailableAlertMessage = getUnavailableAlertMessage(accessoriesList);

  if (unavailableAlertMessage) {
    const { accessoryAlertMessage, accessoryAlertHeadline, accessoryAlertStatus } = unavailableAlertMessage;

    return (
      <div
        className="sui-w-full sui-flex sui-flex-col sui-gap-4"
        data-testid="accessory-unavailable"
      >
        <BundleAlertmessage
          bundleProduct={product}
          size="large"
          content={{ main: accessoryAlertMessage }}
          headline={accessoryAlertHeadline}
          status={accessoryAlertStatus}
        />
      </div>
    );
  }

  return (
    <div data-component="BundleAlert">
      <BundleVariantChangeAlert
        bundleProduct={product}
        alertSize={alertSize}
        alertBodyText={alertBodyText}
        alertHeadline={alertHeadline}
      />
    </div>
  );
};

BundleAlert.propTypes = {
  alertSize: string,
  alertBodyText: string,
  alertHeadline: string,
  product: shape({}).isRequired,
  products: arrayOf(
    shape({})
  ).isRequired,
  accessories: arrayOf(
    shape({
      categoryName: string.isRequired,
      offerings: arrayOf(
        shape({
          offeringName: string.isRequired,
          products: arrayOf(
            shape({
              itemId: string,
              quantity: number,
              type: string
            })
          )
        })
      )
    })
  )
};

BundleAlert.displayName = 'BundleAlert';

export { BundleAlert };